import type {
  BloquePublico,
  PresentacionBloque,
  NotaLateral,
  NotaPublicaCard,
  PublicAds,
  PublicArticleResponse,
  PublicCategoryResponse,
  PublicHomeResponse,
  PublicNotFoundResponse,
  PublicSearchResponse,
  PublicSeccionResponse,
  PublicShell,
} from '@ahora-patagonia/contracts';

/**
 * Datos de muestra para la matriz de verificación del handoff (390/768/1440 × 5/7/9
 * bloques × estados), sin depender de la API ni del seed. Se activan por entorno:
 *
 *   VITE_PUBLIC_FIXTURES=7            cantidad de bloques (5 | 7 | 9)
 *   VITE_PUBLIC_FIXTURES_ESTADO=...   'portada4' | 'sin-avisos' | 'todos' | 'vacia' | 'cuadricula'
 *
 * Los repositories consultan `fixturesActivos()` antes de pegarle a la API. En producción
 * la variable no existe y este archivo queda fuera del camino.
 */

interface OpcionesFixture {
  bloques: number;
  estado: string | null;
}

function env(nombre: string): string | undefined {
  const meta = (import.meta as { env?: Record<string, string | undefined> }).env;
  if (meta?.[nombre] !== undefined) return meta[nombre];
  return typeof process !== 'undefined' ? process.env?.[nombre] : undefined;
}

export function fixturesActivos(): OpcionesFixture | null {
  const valor = env('VITE_PUBLIC_FIXTURES');
  if (!valor) return null;
  const bloques = Number(valor);
  return {
    bloques: Number.isFinite(bloques) && bloques >= 3 ? bloques : 7,
    estado: env('VITE_PUBLIC_FIXTURES_ESTADO') ?? null,
  };
}

/* ─────────────────────────── generadores ─────────────────────────── */

/** Placeholder gris embebido (sin red): la caja rayada del prototipo, en data URI. */
function svgGris(w: number, h: number): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
    `<rect width="100%" height="100%" fill="#BAB6B6"/>` +
    `<line x1="0" y1="0" x2="${w}" y2="${h}" stroke="#9B9797" stroke-width="2"/>` +
    `<line x1="${w}" y1="0" x2="0" y2="${h}" stroke="#9B9797" stroke-width="2"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const TITULARES = [
  'Alerta naranja por viento zonda en la cordillera: ráfagas de hasta 120 kilómetros por hora',
  'El municipio licita la ampliación de la costanera',
  'Temporada récord: los hoteles ya están al 92 % para agosto',
  'Un estudio midió la calidad del agua en los siete lagos',
  'Vuelve la feria de productores al gimnasio municipal',
  'La ruta 40 estará cortada por voladura de rocas el jueves',
  'Cinco proyectos de energía eólica esperan aprobación',
  'El club de la ciudad asciende después de once años',
];

let contador = 0;
function nota(categoria: { id: string; name: string; urlKey: string }): NotaPublicaCard {
  contador += 1;
  const dia = String((contador % 27) + 1).padStart(2, '0');
  return {
    id: `6e4c1f2a-0000-4000-8000-${String(contador).padStart(12, '0')}`,
    title: TITULARES[contador % TITULARES.length]!,
    urlKey: `nota-de-muestra-${contador}`,
    path: `/2026/07/nota-de-muestra-${contador}`,
    excerpt:
      contador % 3 === 0
        ? null
        : 'Una bajada de muestra que resume la nota en una o dos líneas, como la cargaría la redacción.',
    publishedAt: `2026-07-${dia}T12:${String(contador % 60).padStart(2, '0')}:00.000Z`,
    author: 'Redacción',
    categories: [categoria],
    cover: {
      url: svgGris(800, 450),
      alt: 'Imagen de muestra',
      width: 800,
      height: 450,
      variants: null,
    },
  };
}

const CATEGORIAS = [
  { id: '22222222-0000-4000-8000-000000000001', name: 'Política', urlKey: 'politica' },
  { id: '22222222-0000-4000-8000-000000000002', name: 'Economía', urlKey: 'economia' },
  { id: '22222222-0000-4000-8000-000000000003', name: 'Sociedad', urlKey: 'sociedad' },
  { id: '22222222-0000-4000-8000-000000000004', name: 'Deportes', urlKey: 'deportes' },
  { id: '22222222-0000-4000-8000-000000000005', name: 'Turismo', urlKey: 'turismo' },
  { id: '22222222-0000-4000-8000-000000000006', name: 'Policiales', urlKey: 'policiales' },
  { id: '22222222-0000-4000-8000-000000000007', name: 'Cultura', urlKey: 'cultura' },
];

function shell(): PublicShell {
  return {
    tenant: {
      nombre: 'Ahora Patagonia',
      slug: 'ahora-patagonia',
      domain: null,
      logoUrl: null,
      primaryColor: '#1B3A5F',
      secondaryColor: null,
    },
    // Las categorías en la cabecera (como queda un medio recién instalado) y las dos secciones
    // propias afuera, para ver el caso normal: la fila curada y "Más" con el resto.
    nav: [
      ...CATEGORIAS.map((c, i) => ({
        key: c.id,
        name: c.name,
        path: `/${c.urlKey}`,
        articleCount: 12 - i,
        enElHeader: true,
      })),
      { key: 'nav-ultima-hora', name: 'Última hora', path: '/ultima-hora', articleCount: null, enElHeader: false },
      { key: 'nav-mas-leidas', name: 'Más leídas', path: '/mas-leidas', articleCount: null, enElHeader: false },
    ],
    socialLinks: [],
    ultimaHoraNombre: 'Última hora',
    masLeidasNombre: 'Más leídas',
  };
}

function snippet(texto: string, w: number, h: number): string {
  return `<div style="width:100%;max-width:${w}px;height:${h}px;display:flex;align-items:center;justify-content:center;background:#e8e0d8;color:#605d5d;font:14px sans-serif;">${texto}</div>`;
}

function ads(estado: string | null): PublicAds {
  if (estado === 'sin-avisos') return {};
  const base: PublicAds = {
    HEADER: { id: '44444444-0000-4000-8000-000000000001', placement: 'HEADER', type: 'HTML', image: null, targetUrl: null, htmlSnippet: snippet('Marquesina', 970, 250) },
    IN_FEED: { id: '44444444-0000-4000-8000-000000000002', placement: 'IN_FEED', type: 'HTML', image: null, targetUrl: null, htmlSnippet: snippet('Intercalado', 728, 90) },
    FOOTER: { id: '44444444-0000-4000-8000-000000000003', placement: 'FOOTER', type: 'HTML', image: null, targetUrl: null, htmlSnippet: snippet('Zócalo', 728, 90) },
  };
  if (estado === 'todos') {
    base.SIDEBAR = { id: '44444444-0000-4000-8000-000000000004', placement: 'SIDEBAR', type: 'HTML', image: null, targetUrl: null, htmlSnippet: snippet('Robapáginas', 300, 600) };
    base.IN_ARTICLE = { id: '44444444-0000-4000-8000-000000000005', placement: 'IN_ARTICLE', type: 'HTML', image: null, targetUrl: null, htmlSnippet: snippet('Entrenota', 728, 90) };
  }
  return base;
}

function lateral(cards: NotaPublicaCard[]): NotaLateral[] {
  return cards.map((c) => ({ id: c.id, title: c.title, path: c.path, publishedAt: c.publishedAt }));
}

/**
 * Estado `cuadricula`: bloques con disposición y cantidad elegidas, para mirar los chevrones y la
 * grilla sin pasar por el panel. El primero es una tira de 16 (cuatro páginas de 4), el segundo una
 * lista de 18 (tres de 6), el tercero una tira que entra justa en una página (no dibuja chevrones),
 * el cuarto una lista de 18 en una categoría que solo tiene 7 publicadas, el quinto la cuadrícula
 * grande —45 notas, 15 filas de 3, sin chevrones— y el sexto una cuadrícula pedida de 12 en una
 * categoría que tiene 7, para ver la última fila incompleta. El resto queda automático, que es lo que
 * hay que poder ver al lado (y donde la cuadrícula no tiene que aparecer nunca).
 */
function cuadricula(estado: string | null, i: number): PresentacionBloque | null {
  if (estado !== 'cuadricula') return null;
  if (i === 0) return { plantilla: 'TIRA', notas: 16 };
  if (i === 1) return { plantilla: 'LISTA', notas: 18 };
  if (i === 2) return { plantilla: 'TIRA', notas: 4 };
  if (i === 3) return { plantilla: 'LISTA', notas: 18 };
  if (i === 4) return { plantilla: 'CUADRICULA', notas: 45 };
  if (i === 5) return { plantilla: 'CUADRICULA', notas: 12 };
  return null;
}

/** Lo que la categoría llegó a publicar, que no siempre es lo que el editor pidió. */
function cuantasTiene(elegida: PresentacionBloque | null, i: number): number {
  if (!elegida) return 6;
  return i === 3 || i === 5 ? 7 : elegida.notas;
}

export function fixtureHome(opts: OpcionesFixture): PublicHomeResponse {
  contador = 0;
  // `VITE_PUBLIC_FIXTURES=7` son 7 bloques **en total**, que es lo que dice la matriz de
  // verificación: se descuentan los tres que provisiona la plataforma (portada, última hora y más
  // leídas) y el resto son categorías.
  const cantidadCategorias = Math.max(1, opts.bloques - 3);
  const conHuecos = opts.estado === 'portada4';

  const slots = Array.from({ length: 6 }, (_, i) => ({
    position: i + 1,
    // "Portada 4 de 6": huecos en la 3 y la 5, visibles y sin autocompletar.
    nota: conHuecos && (i === 2 || i === 4) ? null : nota(CATEGORIAS[i % CATEGORIAS.length]!),
  }));

  const ultimaHora = Array.from({ length: 15 }, (_, i) =>
    nota(CATEGORIAS[i % CATEGORIAS.length]!),
  );

  const bloques: BloquePublico[] = [
    { kind: 'PORTADA', id: '33333333-0000-4000-8000-000000000001', name: 'Portada', position: 0, maqueta: 'A', slots },
    { kind: 'ULTIMA_HORA', id: '33333333-0000-4000-8000-000000000002', name: 'Última hora', position: 1, notas: ultimaHora },
    // Más leídas con la disposición de fábrica (lista de 6): en el prototipo se ve el bloque lleno.
    // El caso "sin lecturas" no se puede representar acá porque entonces el bloque no existe, y es
    // justamente lo que hay que ver contra la API de verdad.
    {
      kind: 'MAS_LEIDAS',
      id: '33333333-0000-4000-8000-000000000003',
      name: 'Más leídas',
      position: 2,
      notas: Array.from({ length: 6 }, (_, i) => nota(CATEGORIAS[i % CATEGORIAS.length]!)),
      plantilla: null,
    },
    ...CATEGORIAS.slice(0, cantidadCategorias).map((c, i): BloquePublico => {
      const elegida = cuadricula(opts.estado, i);
      return {
        kind: 'CATEGORIA',
        id: `33333333-0000-4000-8000-${String(100 + i).padStart(12, '0')}`,
        name: c.name,
        position: 3 + i,
        categoryId: c.id,
        categoryUrlKey: c.urlKey,
        // Los casos que piden más de lo que la categoría tiene publicado se recortan acá, que es lo
        // que hace la API: el bloque muestra las que hay, con la última fila o página incompleta.
        notas: Array.from({ length: cuantasTiene(elegida, i) }, () => nota(c)),
        plantilla: elegida?.plantilla ?? null,
      };
    }),
  ];

  return { shell: shell(), bloques, ads: ads(opts.estado) };
}

/** `null` = la sección no existe (el repository lo traduce al mismo 404 que la API). */
export function fixtureCategoria(
  urlKey: string,
  opts: OpcionesFixture,
): PublicCategoryResponse | null {
  contador = 100;
  const categoria = CATEGORIAS.find((c) => c.urlKey === urlKey);
  if (!categoria) return null;
  const vacia = opts.estado === 'vacia';
  const items = vacia ? [] : Array.from({ length: 12 }, () => nota(categoria));
  return {
    shell: shell(),
    category: { ...categoria, description: 'Las noticias de la sección, de la más nueva a la más vieja.' },
    items,
    page: 1,
    pageSize: 12,
    total: vacia ? 0 : 30,
    ultimaHora: lateral(Array.from({ length: 20 }, () => nota(categoria))),
    ads: ads(opts.estado),
  };
}

/**
 * `/ultima-hora` y `/mas-leidas` de muestra.
 *
 * Con `estado=vacia` las dos salen sin notas, que es el caso que hay que poder mirar: en Más leídas
 * es el estado real de un medio al que todavía nadie leyó, y es distinto de "no hay notas".
 */
export function fixtureSeccion(
  kind: 'ultima-hora' | 'mas-leidas',
  opts: OpcionesFixture,
): PublicSeccionResponse {
  contador = 300;
  const vacia = opts.estado === 'vacia';
  const items = vacia
    ? []
    : Array.from({ length: 12 }, (_, i) => nota(CATEGORIAS[i % CATEGORIAS.length]!));
  const esUltimaHora = kind === 'ultima-hora';

  return {
    shell: shell(),
    titulo: esUltimaHora ? 'Última hora' : 'Más leídas',
    descripcion: esUltimaHora
      ? 'Todo lo que publicamos, de lo más nuevo a lo más viejo.'
      : 'Lo que más se leyó en las últimas 48 horas.',
    items,
    page: 1,
    pageSize: 12,
    total: vacia ? 0 : esUltimaHora ? 40 : 24,
    // Cada página lleva en la lateral **la otra** sección: la propia repetiría el cuerpo.
    ultimaHora: esUltimaHora ? [] : lateral(Array.from({ length: 20 }, () => nota(CATEGORIAS[0]!))),
    masLeidas: esUltimaHora ? lateral(Array.from({ length: 20 }, () => nota(CATEGORIAS[1]!))) : [],
    ads: ads(opts.estado),
  };
}

export function fixtureBusqueda(q: string, opts: OpcionesFixture): PublicSearchResponse {
  contador = 200;
  const categoria = CATEGORIAS[2]!;
  const items = q === 'nada' ? [] : Array.from({ length: 7 }, () => nota(categoria));
  return {
    shell: shell(),
    q,
    items,
    page: 1,
    pageSize: 12,
    total: items.length,
    ultimaHora: lateral(Array.from({ length: 20 }, () => nota(categoria))),
    ads: ads(opts.estado),
  };
}

export function fixtureNotFound(opts: OpcionesFixture): PublicNotFoundResponse {
  contador = 400;
  const categoria = CATEGORIAS[0]!;
  return {
    shell: shell(),
    ultimas: Array.from({ length: 4 }, () => nota(categoria)),
    ads: ads(opts.estado),
  };
}

export function fixtureNota(opts: OpcionesFixture): PublicArticleResponse {
  contador = 300;
  const categoria = CATEGORIAS[2]!;
  const principal = nota(categoria);
  const parrafo = (texto: string) => ({
    type: 'paragraph' as const,
    content: [{ type: 'text' as const, text: texto }],
  });
  return {
    shell: shell(),
    nota: {
      id: principal.id,
      title:
        'Alerta naranja por viento zonda en la cordillera: ráfagas de hasta 120 kilómetros por hora y clases suspendidas',
      urlKey: principal.urlKey,
      path: principal.path,
      excerpt:
        'Defensa Civil pidió no circular por caminos de montaña hasta el mediodía. El servicio se normalizaría a la tarde.',
      body: {
        type: 'doc',
        content: [
          parrafo('El viento zonda volvió a la cordillera con ráfagas que superaron los 120 kilómetros por hora en la zona alta, según el registro de la estación del cerro.'),
          parrafo('Defensa Civil dispuso la suspensión de clases en los establecimientos del oeste y pidió evitar los caminos de montaña hasta el mediodía.'),
          { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Qué se espera para las próximas horas' }] },
          parrafo('El fenómeno perdería intensidad hacia la tarde, aunque el organismo mantiene la alerta hasta nuevo aviso.'),
          {
            type: 'blockquote',
            content: [parrafo('“La recomendación es no salir si no es imprescindible”, dijo la directora del organismo.')],
          },
          {
            type: 'bulletList',
            content: [
              { type: 'listItem', content: [parrafo('Suspensión de clases en el turno mañana.')] },
              { type: 'listItem', content: [parrafo('Cortes preventivos de energía en tres barrios.')] },
            ],
          },
          {
            type: 'image',
            attrs: {
              mediaId: '55555555-0000-4000-8000-000000000001',
              alt: 'El viento sobre la avenida principal',
              caption: 'La avenida, al mediodía. Foto: Redacción',
              fullWidth: false,
              // Como la resuelve la API a partir del `mediaId`. Vertical y sin variantes: es el
              // caso que el cuerpo tiene que dibujar sin recortar.
              imagen: {
                url: svgGris(600, 800),
                alt: 'El viento sobre la avenida principal',
                width: 600,
                height: 800,
                variants: null,
              },
            },
          },
          parrafo('Los equipos municipales trabajan desde temprano en el retiro de ramas y postes caídos.'),
          { type: 'video', attrs: { provider: 'youtube', videoId: 'dQw4w9WgXcQ', caption: 'El registro de un vecino' } },
          parrafo('La última alerta comparable se registró en julio de 2023, con daños menores.'),
          parrafo('El municipio recordó los teléfonos de emergencia y pidió reportar incidentes solo por esa vía.'),
        ],
      },
      publishedAt: principal.publishedAt,
      author: 'María Fernández',
      categories: [categoria],
      cover: principal.cover,
      seo: { metaTitle: null, metaDescription: null, ogImage: null, canonicalUrl: null },
    },
    masDe: { category: categoria, notas: Array.from({ length: 4 }, () => nota(categoria)) },
    ultimaHora: lateral(Array.from({ length: 20 }, () => nota(categoria))),
    ads: ads(opts.estado),
  };
}
